import { 
    CloudWatchLogsClient, 
    DescribeLogGroupsCommand 
} from '@aws-sdk/client-cloudwatch-logs';
import { 
    CloudWatchClient, 
    GetMetricDataCommand,
    ListMetricsCommand 
} from '@aws-sdk/client-cloudwatch';

// Discovery-based approach: Find all Contact Flows that have reported errors
const discoverContactFlowErrors = async (client, instanceId) => {
  try {
    const command = new ListMetricsCommand({
      Namespace: 'AWS/Connect',
      MetricName: 'ContactFlowErrors',
      Dimensions: [
        {
          Name: 'InstanceId',
          Value: instanceId
        }
      ]
    });

    const response = await client.send(command);
    const discoveredFlows = [];

    if (response.Metrics) {
      response.Metrics.forEach((metric, index) => {
        // Extract dimensions to understand the structure
        const dimensions = {};
        metric.Dimensions?.forEach(dim => {
          dimensions[dim.Name] = dim.Value;
        });
        
        // Store the flow info with exact dimensions for later querying
        discoveredFlows.push({
          flowName: dimensions.ContactFlowName || dimensions.ContactFlowId || 'Unknown',
          dimensions: metric.Dimensions,
          metricName: metric.MetricName,
          namespace: metric.Namespace
        });
      });
    }

    return discoveredFlows;
  } catch (error) {
    console.error('Error discovering Contact Flow errors:', error);
    return [];
  }
};

// Fetch error counts for discovered flows using their exact dimensions
const fetchDiscoveredErrorMetrics = async (client, discoveredFlows, startTime, endTime) => {
  if (discoveredFlows.length === 0) {
    return new Map();
  }
  
  const errorMetricsMap = new Map();
  const batchSize = 25; // Stay conservative with batch size

  for (let i = 0; i < discoveredFlows.length; i += batchSize) {
    const batch = discoveredFlows.slice(i, i + batchSize);
    
    try {
      const metricQueries = batch.map((flow, batchIndex) => ({
        Id: `discovered_err${batchIndex}`,
        MetricStat: {
          Metric: {
            Namespace: flow.namespace,
            MetricName: flow.metricName,
            Dimensions: flow.dimensions // Use exact dimensions from discovery
          },
          Period: 3600,
          Stat: 'Sum'
        },
        ReturnData: true
      }));

      const command = new GetMetricDataCommand({
        MetricDataQueries: metricQueries,
        StartTime: startTime,
        EndTime: endTime
      });

      const response = await client.send(command);
      
      if (response.MetricDataResults) {
        const resultMap = new Map();
        response.MetricDataResults.forEach(result => {
          resultMap.set(result.Id, result);
        });
        
        // Process each flow in the batch
        for (let batchIndex = 0; batchIndex < batch.length; batchIndex++) {
          const flow = batch[batchIndex];
          const errId = `discovered_err${batchIndex}`;
          
          const errResult = resultMap.get(errId);
          const errTotal = errResult && errResult.Values ? 
            errResult.Values.reduce((sum, val) => sum + (val || 0), 0) : 0;
          
          errorMetricsMap.set(flow.flowName, {
            errors30Days: Math.round(errTotal),
            flowName: flow.flowName,
            discoveredDimensions: flow.dimensions
          });
        }
      }
      
      // Add delay between batches
      if (i + batchSize < discoveredFlows.length) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
    } catch (error) {
      console.error(`Failed to fetch discovered error metrics for batch starting at index ${i}:`, error);
    }
  }

  return errorMetricsMap;
};


export const fetchContactFlowErrorMetrics = async (credentials, region, contactFlows, instanceId) => {
  const client = new CloudWatchClient({
    region,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
  });

  const errorMetricsMap = new Map();
  
  // Initialize all contact flows with zero error counts
  contactFlows.forEach(flow => {
    errorMetricsMap.set(flow.Id, {
      errors30Days: 0,
      flowName: flow.Name
    });
  });

  if (contactFlows.length === 0) {
    return errorMetricsMap;
  }

  // Set up time range for last 30 days
  const endTime = new Date();
  const startTime = new Date();
  startTime.setDate(startTime.getDate() - 30);

  try {
    // Phase 1: Discover all flows that have reported errors
    const discoveredFlows = await discoverContactFlowErrors(client, instanceId);
    
    if (discoveredFlows.length === 0) {
      return errorMetricsMap;
    }

    // Phase 2: Fetch error counts for discovered flows
    const discoveredErrorMetrics = await fetchDiscoveredErrorMetrics(client, discoveredFlows, startTime, endTime);
    
    // Phase 3: Client-side matching - populate error counts for matching flows
    let matchedFlows = 0;
    let totalErrors = 0;
    
    contactFlows.forEach(flow => {
      // Try matching by exact name first
      let discoveredData = discoveredErrorMetrics.get(flow.Name);
      
      if (!discoveredData) {
        // Try case-insensitive matching
        for (const [discoveredName, data] of discoveredErrorMetrics.entries()) {
          if (discoveredName.toLowerCase() === flow.Name.toLowerCase()) {
            discoveredData = data;
            break;
          }
        }
      }
      
      if (discoveredData && discoveredData.errors30Days > 0) {
        errorMetricsMap.set(flow.Id, {
          errors30Days: discoveredData.errors30Days,
          flowName: flow.Name,
          discoveredAs: discoveredData.flowName
        });
        matchedFlows++;
        totalErrors += discoveredData.errors30Days;
      }
    });
    
    console.log(`Contact Flow error metrics: ${matchedFlows}/${contactFlows.length} flows matched, ${totalErrors} total errors found`);
    
  } catch (error) {
    console.error('Error in discovery-based Contact Flow error metrics:', error);
  }
  
  return errorMetricsMap;
};

// Unified error metrics function for BOTH Contact Flows AND Contact Flow Modules
export const fetchUnifiedContactFlowErrorMetrics = async (credentials, region, contactFlows = [], contactFlowModules = [], instanceId) => {
  const client = new CloudWatchClient({
    region,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
  });

  // Initialize all items with zero error counts
  contactFlows.forEach(flow => {
    flow.ContactFlowErrors30Days = 0;
  });
  
  contactFlowModules.forEach(module => {
    module.ContactFlowErrors30Days = 0;
  });

  if (contactFlows.length === 0 && contactFlowModules.length === 0) {
    return { contactFlows, contactFlowModules };
  }

  // Set up time range for last 30 days
  const endTime = new Date();
  const startTime = new Date();
  startTime.setDate(startTime.getDate() - 30);

  try {
    // Phase 1: Discover all flows/modules that have reported errors
    const discoveredFlows = await discoverContactFlowErrors(client, instanceId);
    
    if (discoveredFlows.length === 0) {
      return { contactFlows, contactFlowModules };
    }

    // Phase 2: Fetch error counts for discovered flows/modules
    const discoveredErrorMetrics = await fetchDiscoveredErrorMetrics(client, discoveredFlows, startTime, endTime);
    
    // Phase 3: Client-side matching for BOTH flows and modules
    let flowMatches = 0;
    let moduleMatches = 0;
    let totalErrors = 0;
    
    // Match Contact Flows
    contactFlows.forEach(flow => {
      const discoveredData = findDiscoveredMatch(discoveredErrorMetrics, flow.Name);
      if (discoveredData && discoveredData.errors30Days > 0) {
        flow.ContactFlowErrors30Days = discoveredData.errors30Days;
        flowMatches++;
        totalErrors += discoveredData.errors30Days;
      }
    });
    
    // Match Contact Flow Modules  
    contactFlowModules.forEach(module => {
      const discoveredData = findDiscoveredMatch(discoveredErrorMetrics, module.Name);
      if (discoveredData && discoveredData.errors30Days > 0) {
        module.ContactFlowErrors30Days = discoveredData.errors30Days;
        moduleMatches++;
        totalErrors += discoveredData.errors30Days;
      }
    });
    
    console.log(`Unified error metrics: ${flowMatches} flows and ${moduleMatches} modules matched, ${totalErrors} total errors found`);
    
  } catch (error) {
    console.error('Error in unified Contact Flow/Module error metrics:', error);
  }
  
  return { contactFlows, contactFlowModules };
};

// Helper function to find discovered match with case-insensitive fallback
const findDiscoveredMatch = (discoveredErrorMetrics, itemName) => {
  // Try exact match first
  let discoveredData = discoveredErrorMetrics.get(itemName);
  
  if (!discoveredData) {
    // Try case-insensitive matching
    for (const [discoveredName, data] of discoveredErrorMetrics.entries()) {
      if (discoveredName.toLowerCase() === itemName.toLowerCase()) {
        return data;
      }
    }
  }
  
  return discoveredData;
};

export const fetchLogGroups = async (credentials, region) => {
  const client = new CloudWatchLogsClient({
    region,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
  });

  const allLogGroups = [];
  let nextToken = undefined;

  do {
    const command = new DescribeLogGroupsCommand({
      limit: 50, // CloudWatch Logs uses 'limit' instead of 'MaxResults'
      nextToken: nextToken,
    });

    const response = await client.send(command);
    
    if (response.logGroups) {
      allLogGroups.push(...response.logGroups);
    }
    
    nextToken = response.nextToken;
  } while (nextToken);

  return allLogGroups;
};

// Helper function to scan a specific time period
const scanInvocationPeriod = async (client, functionNames, daysBack, granularitySeconds, scanLabel) => {
  const invocationMap = new Map();
  
  // Initialize all functions
  functionNames.forEach(name => {
    invocationMap.set(name, {
      lastInvocation: null,
      lastInvocationDisplay: 'Never',
      maxDaysScanned: daysBack // Track how far back we scanned for this result
    });
  });

  if (functionNames.length === 0) {
    return invocationMap;
  }

  // Process functions in batches of 100 (CloudWatch GetMetricData limit is 500 queries)
  const batchSize = 100;
  // let foundCount = 0; // eslint-disable-line no-unused-vars

  for (let i = 0; i < functionNames.length; i += batchSize) {
    const batch = functionNames.slice(i, i + batchSize);
    
    try {
      const metricQueries = batch.map((functionName, index) => ({
        Id: `m${i + index}`, // Unique identifier for this query
        MetricStat: {
          Metric: {
            Namespace: 'AWS/Lambda',
            MetricName: 'Invocations',
            Dimensions: [
              {
                Name: 'FunctionName',
                Value: functionName
              }
            ]
          },
          Period: granularitySeconds,
          Stat: 'Sum'
        },
        ReturnData: true
      }));

      // Query the specified time period
      const endTime = new Date();
      const startTime = new Date();
      startTime.setDate(startTime.getDate() - daysBack);

      const command = new GetMetricDataCommand({
        MetricDataQueries: metricQueries,
        StartTime: startTime,
        EndTime: endTime,
        ScanBy: 'TimestampDescending' // Get most recent data first
      });

      const response = await client.send(command);
      
      if (response.MetricDataResults) {
        processMetricResults(response.MetricDataResults, batch, invocationMap, daysBack);
      }
      
      // Add a small delay between batches to avoid throttling
      if (i + batchSize < functionNames.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
    } catch (error) {
      console.warn(`${scanLabel}: Failed to fetch invocation metrics for batch starting at index ${i}:`, error.message);
      // Continue with next batch rather than failing completely
    }
  }
  return invocationMap;
};

export const fetchLambdaInvocationMetrics = async (credentials, region, functionNames, options = {}) => {
  const {
    // Configurable scanning tiers
    quickScanDays = 30,           // Recent activity scan
    mediumScanDays = 180,         // Default comprehensive scan  
    deepScanDays = 455,           // Maximum CloudWatch retention
    
    // Performance options
    enableSmartTiering = true,    // Use tiered scanning approach
    enableEarlyExit = true,       // Skip deeper scans if enough found
    earlyExitThreshold = 0.8,     // Stop if 80% of functions found
    
    // Granularity options
    recentGranularity = 3600,     // 1-hour for recent data
    oldGranularity = 86400,       // 1-day for old data (faster)
    
    // Debug options
    enableDebugLogging = false // eslint-disable-line no-unused-vars
  } = options;

  const client = new CloudWatchClient({
    region,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
  });

  const allInvocationMap = new Map();
  let totalFunctionsWithData = 0;

  // Helper function to format "Never" with context
  const formatNeverWithContext = (daysScanned) => {
    if (daysScanned <= 30) {
      return 'None in the last 30 days';
    } else if (daysScanned <= 90) {
      return 'None in the last 3 months';
    } else if (daysScanned <= 180) {
      return 'None in the last 6 months';
    } else if (daysScanned <= 365) {
      return 'None in the last 12 months';
    } else {
      const years = Math.floor(daysScanned / 365);
      const months = Math.floor((daysScanned % 365) / 30);
      if (months > 0) {
        return `None in the last ${years} year${years > 1 ? 's' : ''} ${months} month${months > 1 ? 's' : ''}`;
      } else {
        return `None in the last ${years} year${years > 1 ? 's' : ''}`;
      }
    }
  };

  // Initialize all functions as 'Never' invoked
  functionNames.forEach(name => {
    allInvocationMap.set(name, {
      lastInvocation: null,
      lastInvocationDisplay: 'Never',
      maxDaysScanned: 0
    });
  });

  if (functionNames.length === 0) {
    return allInvocationMap;
  }

  if (!enableSmartTiering) {
    // Simple single-tier scan
    const results = await scanInvocationPeriod(client, functionNames, mediumScanDays, recentGranularity, 'Single Scan');
    
    // Update "Never" results with context
    results.forEach((value, key) => {
      if (value.lastInvocationDisplay === 'Never') {
        value.lastInvocationDisplay = formatNeverWithContext(mediumScanDays);
      }
    });
    
    return results;
  }

  // Smart tiered scanning approach
  
  const quickResults = await scanInvocationPeriod(
    client, 
    functionNames, 
    quickScanDays, 
    recentGranularity, 
    'Quick Scan'
  );
  
  // Merge results and count found functions
  let functionsWithData = 0;
  quickResults.forEach((value, key) => {
    allInvocationMap.set(key, value);
    if (value.lastInvocationDisplay !== 'Never') {
      functionsWithData++;
    }
  });
  
  totalFunctionsWithData = functionsWithData;
  
  // Check for early exit
  const foundPercentage = functionsWithData / functionNames.length;
  if (enableEarlyExit && foundPercentage >= earlyExitThreshold) {
    // Update remaining "Never" results with quick scan context
    allInvocationMap.forEach((value, key) => {
      if (value.lastInvocationDisplay === 'Never') {
        value.lastInvocationDisplay = formatNeverWithContext(quickScanDays);
      }
    });
    
    return allInvocationMap;
  }

  // Phase 2: Medium scan for unfound functions (180 days, 1-hour granularity)
  const unfoundFunctions = functionNames.filter(name => 
    allInvocationMap.get(name).lastInvocationDisplay === 'Never'
  );
  
  if (unfoundFunctions.length > 0 && mediumScanDays > quickScanDays) {
    const mediumResults = await scanInvocationPeriod(
      client, 
      unfoundFunctions, 
      mediumScanDays, 
      recentGranularity, 
      'Medium Scan'
    );
    
    // Merge results
    let newlyFound = 0;
    mediumResults.forEach((value, key) => {
      if (value.lastInvocationDisplay !== 'Never') {
        allInvocationMap.set(key, value);
        newlyFound++;
      } else {
        // Update the max days scanned for this function
        const existing = allInvocationMap.get(key);
        existing.maxDaysScanned = mediumScanDays;
        allInvocationMap.set(key, existing);
      }
    });
    
    totalFunctionsWithData += newlyFound;
    
    // Check for early exit again
    const newFoundPercentage = totalFunctionsWithData / functionNames.length;
    if (enableEarlyExit && newFoundPercentage >= earlyExitThreshold) {
      // Update remaining "Never" results with medium scan context
      allInvocationMap.forEach((value, key) => {
        if (value.lastInvocationDisplay === 'Never') {
          value.lastInvocationDisplay = formatNeverWithContext(mediumScanDays);
        }
      });
      
      return allInvocationMap;
    }
  }

  // Phase 3: Deep scan for still-unfound functions (455 days, daily granularity)
  const stillUnfoundFunctions = functionNames.filter(name => 
    allInvocationMap.get(name).lastInvocationDisplay === 'Never'
  );
  
  if (stillUnfoundFunctions.length > 0 && deepScanDays > mediumScanDays) {
    const deepResults = await scanInvocationPeriod(
      client, 
      stillUnfoundFunctions, 
      deepScanDays, 
      oldGranularity, // Use daily granularity for old data (faster)
      'Deep Scan'
    );
    
    // Merge final results
    let finalNewlyFound = 0;
    deepResults.forEach((value, key) => {
      if (value.lastInvocationDisplay !== 'Never') {
        allInvocationMap.set(key, value);
        finalNewlyFound++;
      } else {
        // Update the max days scanned for this function
        const existing = allInvocationMap.get(key);
        existing.maxDaysScanned = deepScanDays;
        allInvocationMap.set(key, existing);
      }
    });
    
    totalFunctionsWithData += finalNewlyFound;
  }

  // Final update: Apply contextual "Never" messages based on actual scan depth
  allInvocationMap.forEach((value, key) => {
    if (value.lastInvocationDisplay === 'Never') {
      let maxScanDepth = quickScanDays; // Default to quick scan
      
      // Determine actual scan depth for this function
      if (unfoundFunctions.includes(key)) {
        maxScanDepth = mediumScanDays;
        if (stillUnfoundFunctions.includes(key)) {
          maxScanDepth = deepScanDays;
        }
      }
      
      value.lastInvocationDisplay = formatNeverWithContext(maxScanDepth);
      value.maxDaysScanned = maxScanDepth;
    }
  });

  return allInvocationMap;
};

export const fetchLambda30DayMetrics = async (credentials, region, functionNames) => {
  const client = new CloudWatchClient({
    region,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
  });

  const metricsMap = new Map();
  
  // Initialize all functions with zero counts
  functionNames.forEach(name => {
    metricsMap.set(name, {
      invocations30Days: 0,
      errors30Days: 0
    });
  });

  if (functionNames.length === 0) {
    return metricsMap;
  }

  // Set up time range for last 30 days
  const endTime = new Date();
  const startTime = new Date();
  startTime.setDate(startTime.getDate() - 30);

  // MUCH smaller batch size to avoid CloudWatch API issues
  // Each function needs 2 queries (invocations + errors), so 25 functions = 50 queries per batch
  const batchSize = 25; 
  let errorCount = 0;

  for (let i = 0; i < functionNames.length; i += batchSize) {
    const batch = functionNames.slice(i, i + batchSize);
    
    try {
      // Create metric queries
      const metricQueries = [];
      
      batch.forEach((functionName, batchIndex) => {
        const globalIndex = i + batchIndex;  // Use global position across all functions
        const invId = `inv${globalIndex}`;
        const errId = `err${globalIndex}`;
        
        metricQueries.push({
          Id: invId,
          MetricStat: {
            Metric: {
              Namespace: 'AWS/Lambda',
              MetricName: 'Invocations',
              Dimensions: [
                {
                  Name: 'FunctionName',
                  Value: functionName
                }
              ]
            },
            Period: 3600,
            Stat: 'Sum'
          },
          ReturnData: true
        });
        
        metricQueries.push({
          Id: errId,
          MetricStat: {
            Metric: {
              Namespace: 'AWS/Lambda',
              MetricName: 'Errors',
              Dimensions: [
                {
                  Name: 'FunctionName',
                  Value: functionName
                }
              ]
            },
            Period: 3600,
            Stat: 'Sum'
          },
          ReturnData: true
        });
      });

      const command = new GetMetricDataCommand({
        MetricDataQueries: metricQueries,
        StartTime: startTime,
        EndTime: endTime
      });

      const response = await client.send(command);
      
      if (response.MetricDataResults) {
        // Verify we got all expected results
        if (response.MetricDataResults.length !== metricQueries.length) {
          console.warn(`Missing results: expected ${metricQueries.length}, got ${response.MetricDataResults.length}`);
        }
        
        // Create a map of results by ID for easy lookup
        const resultMap = new Map();
        response.MetricDataResults.forEach(result => {
          resultMap.set(result.Id, result);
        });
        
        // Track missing results for this batch
        let batchMissingInvocations = 0;
        let batchMissingErrors = 0;
        
        // Process each function in the batch
        for (let batchIndex = 0; batchIndex < batch.length; batchIndex++) {
          const functionName = batch[batchIndex];
          const globalIndex = i + batchIndex;  // Use same global index as during query creation
          const invId = `inv${globalIndex}`;
          const errId = `err${globalIndex}`;
          
          // Get invocation data
          const invResult = resultMap.get(invId);
          const invTotal = invResult && invResult.Values ? 
            invResult.Values.reduce((sum, val) => sum + (val || 0), 0) : 0;
          
          // Get error data
          const errResult = resultMap.get(errId);
          const errTotal = errResult && errResult.Values ? 
            errResult.Values.reduce((sum, val) => sum + (val || 0), 0) : 0;
          
          // Track missing results
          if (!invResult) {
            batchMissingInvocations++;
          }
          if (!errResult) {
            batchMissingErrors++;
          }
          
          // Store the results
          metricsMap.set(functionName, {
            invocations30Days: Math.round(invTotal),
            errors30Days: Math.round(errTotal)
          });
        }
        
        // Track batch completion issues
        if (batchMissingInvocations > 0 || batchMissingErrors > 0) {
          console.warn(`Batch processing: missing ${batchMissingInvocations} invocation results and ${batchMissingErrors} error results`);
        }
        
      }
      
      // Longer delay between batches to be more gentle on CloudWatch API
      if (i + batchSize < functionNames.length) {
        await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
      }
      
    } catch (error) {
      console.error(`Failed to fetch 30-day metrics for batch starting at index ${i}:`, error);
      errorCount++;
    }
  }

  // Final validation
  const functionsWithInvocations = Array.from(metricsMap.entries()).filter(([name, data]) => 
    data.invocations30Days > 0
  ).length;
  
  const functionsWithErrors = Array.from(metricsMap.entries()).filter(([name, data]) => 
    data.errors30Days > 0
  ).length;
  
  console.log(`30-day Lambda metrics: ${functionsWithInvocations}/${functionNames.length} functions with invocations, ${functionsWithErrors}/${functionNames.length} with errors`);
  
  if (errorCount > 0) {
    console.warn(`Warning: ${errorCount} batch errors occurred during metrics collection`);
  }
  
  return metricsMap;
};

export const createLogGroupRetentionMap = (logGroups) => {
  const retentionMap = new Map();
  
  logGroups.forEach(logGroup => {
    const logGroupName = logGroup.logGroupName;
    const retentionInDays = logGroup.retentionInDays;
    
    // Convert retention to a readable format
    let retentionDisplay;
    if (retentionInDays === undefined || retentionInDays === null) {
      retentionDisplay = 'Never Expire';
    } else if (retentionInDays === 1) {
      retentionDisplay = '1 day';
    } else if (retentionInDays < 30) {
      retentionDisplay = `${retentionInDays} days`;
    } else if (retentionInDays === 30) {
      retentionDisplay = '1 month';
    } else if (retentionInDays < 365) {
      const months = Math.floor(retentionInDays / 30);
      retentionDisplay = `${months} month${months > 1 ? 's' : ''}`;
    } else {
      const years = Math.floor(retentionInDays / 365);
      retentionDisplay = `${years} year${years > 1 ? 's' : ''}`;
    }
    
    retentionMap.set(logGroupName, {
      retentionInDays: retentionInDays,
      retentionDisplay: retentionDisplay,
      logGroupArn: logGroup.arn || '',
      creationTime: logGroup.creationTime || null,
      storedBytes: logGroup.storedBytes || 0
    });
  });
  
  return retentionMap;
};

const processMetricResults = (results, batch, invocationMap, daysBack) => {
  let newlyFoundCount = 0;

  results.forEach((result, index) => {
    const functionName = batch[index];
    let lastInvocationTime = null;

    if (result.Timestamps && result.Values) {
      for (let j = 0; j < result.Values.length; j++) {
        if (result.Values[j] > 0) {
          lastInvocationTime = result.Timestamps[j];
          break;
        }
      }
    }

    let displayValue = 'Never';
    if (lastInvocationTime) {
      newlyFoundCount++;
      const now = new Date();
      const invocationDate = new Date(lastInvocationTime);
      const daysDiff = Math.floor((now - invocationDate) / (1000 * 60 * 60 * 24));

      if (daysDiff === 0) {
        displayValue = 'Today';
      } else if (daysDiff === 1) {
        displayValue = '1 day ago';
      } else if (daysDiff < 30) {
        displayValue = `${daysDiff} days ago`;
      } else if (daysDiff < 365) {
        const months = Math.floor(daysDiff / 30);
        displayValue = `${months} month${months > 1 ? 's' : ''} ago`;
      } else {
        const years = Math.floor(daysDiff / 365);
        displayValue = `${years} year${years > 1 ? 's' : ''} ago`;
      }
    }

    invocationMap.set(functionName, {
      lastInvocation: lastInvocationTime,
      lastInvocationDisplay: displayValue,
      maxDaysScanned: daysBack
    });
  });

  return newlyFoundCount;
};

export const debugSingleFunctionMetrics = async (credentials, region, functionName) => {
  const client = new CloudWatchClient({
    region,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
  });

  const endTime = new Date();
  const startTime = new Date();
  startTime.setDate(startTime.getDate() - 30);

  const command = new GetMetricDataCommand({
    MetricDataQueries: [
      {
        Id: 'inv1',
        MetricStat: {
          Metric: {
            Namespace: 'AWS/Lambda',
            MetricName: 'Invocations',
            Dimensions: [{ Name: 'FunctionName', Value: functionName }]
          },
          Period: 3600,
          Stat: 'Sum'
        },
        ReturnData: true
      },
      {
        Id: 'err1',
        MetricStat: {
          Metric: {
            Namespace: 'AWS/Lambda',
            MetricName: 'Errors',
            Dimensions: [{ Name: 'FunctionName', Value: functionName }]
          },
          Period: 3600,
          Stat: 'Sum'
        },
        ReturnData: true
      }
    ],
    StartTime: startTime,
    EndTime: endTime
  });

  const response = await client.send(command);
  
  response.MetricDataResults?.forEach(result => {
    const total = result.Values?.reduce((sum, val) => sum + val, 0) || 0;
    console.log(`${result.Id}: ${total} total, ${result.Values?.length || 0} data points`);
  });
};